import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, signal, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  Tour,
  Tournament,
  Country,
  State,
  TournamentScoringType,
  TournamentClassification,
} from '../../../models';
import {
  TournamentService,
  TourService,
} from '../../../services';

import { ToastrService } from 'ngx-toastr';
import { ImportService } from '../../../services/import/import.service';
import { RefService } from '../../../services/reference/reference.service';
import { forkJoin, Observable, switchMap, tap, Subscription } from 'rxjs';
import { AlertContainerComponent } from '../../shared/alert-container/alert-container.component';
import { AlertService } from '../../../services/shared/alert/alert.service';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { AuditInfoComponent } from '../../shared/audit-info/audit-info.component';
import { ScoringRealtimeService, TournamentActivityData } from '../../../services/signalr/scoring-realtime.service';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { ModulesTournamentsApplicationTournamentExternalIdsTournamentExternalIdRequest } from '../../../api-client';

interface ExternalIdViewModel {
  organization: string;
  externalId: string;
  isEditing: boolean;
}

@Component({
  selector: 'app-tournament-detail',
  standalone: true,
  imports: [
    FormsModule,
    AlertContainerComponent,
    ConfirmationDialogComponent,
    AuditInfoComponent
],
  templateUrl: './tournament-detail.component.html',
  styleUrls: ['./tournament-detail.component.scss'],
  // Using OnPush for performance; manual change detection may be needed for some async updates
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TournamentDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('lpgaIdInput') lpgaIdInput!: ElementRef<HTMLInputElement>;

  tournament: Tournament = {} as Tournament;
  countries: Country[] = [];
  states: State[] = [];
  tours: Tour[] = [];
  tournamentScoringTypes: TournamentScoringType[] = [];
  tournamentClassifications: TournamentClassification[] = [];
  externalIds: ExternalIdViewModel[] = [];
  availableYears: string[] = this.generateAvailableYears();
  timeOffsets: string[] = this.generateTimeOffsetOptions();

  // Track dirty state for form changes
  isDirty = signal(false);
  isSaving = signal(false);

  // Track previous status for change detection
  private previousStatus: string = '';

  // Track if user is actively editing purse to prevent formatting interference
  private isPurseBeingEdited: boolean = false;

  // Track if user is actively editing tournament number
  private isNumberBeingEdited: boolean = false;

  // Track if user is actively editing LPGA ID
  private isLpgaIdBeingEdited: boolean = false;

  // Computed properties for editable settings
  get status(): string {
    // Check direct property first, then fall back to settings dictionary
    return this.tournament.status || this.tournament.settings?.['Status'] || '';
  }

  set status(value: string) {
    // Update both the direct property and the settings dictionary
    this.tournament.status = value;
    this.updateSetting('Status', value);
    this.markDirty();
  }

  get statusReason(): string {
    // Check direct property first, then fall back to settings dictionary
    return this.tournament.statusReason || this.tournament.settings?.['StatusReason'] || '';
  }

  set statusReason(value: string) {
    // Update both the direct property and the settings dictionary
    this.tournament.statusReason = value;
    this.updateSetting('StatusReason', value);
    this.markDirty();
  }

  get timeOffset(): string {
    // Check direct property first, then fall back to settings dictionary
    const value = this.tournament.timeOffset || this.tournament.settings?.['TimeOffset'] || '';

    // Normalize the format to match dropdown options (e.g., "-06" -> "-06:00")
    if (value && !value.includes(':')) {
      return value + ':00';
    }

    return value;
  }

  set timeOffset(value: string) {
    // Update both the direct property and the settings dictionary
    this.tournament.timeOffset = value;
    this.updateSetting('TimeOffset', value);
    this.markDirty();
  }

  get purseFormatted(): string {
    // Don't format while user is editing - show raw value
    if (this.isPurseBeingEdited) {
      return this.tournament.purse?.toString() || '';
    }

    if (!this.tournament.purse) return '';
    // Format with commas and 2 decimal places
    return this.tournament.purse.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  set purseFormatted(value: string) {
    // Remove commas and trim whitespace
    const cleaned = value.replace(/,/g, '').trim();

    if (cleaned === '') {
      // Empty string defaults to 0 (matching legacy WPF Admin behavior)
      this.tournament.purse = 0;
      this.markDirty();
    } else {
      const numericValue = parseFloat(cleaned);
      if (!isNaN(numericValue)) {
        this.tournament.purse = numericValue;
        this.markDirty();
      }
    }
  }

  onPurseFocus(): void {
    this.isPurseBeingEdited = true;
  }

  onPurseBlur(): void {
    this.isPurseBeingEdited = false;
    // Trigger change detection to reformat the value
    this.cdr.markForCheck();
  }

  get numberFormatted(): string {
    if (!this.tournament.number && this.tournament.number !== 0) return '';
    return this.tournament.number.toString();
  }

  set numberFormatted(value: string) {
    // Remove any non-digit characters
    const cleaned = value.replace(/\D/g, '');

    if (cleaned === '') {
      // Empty string defaults to 0
      this.tournament.number = 0;
      this.markDirty();
    } else {
      const numericValue = parseInt(cleaned, 10);
      if (!isNaN(numericValue) && numericValue <= 2147483647) {
        this.tournament.number = numericValue;
        this.markDirty();
      }
      // If value exceeds max, don't update (silently ignore)
    }
  }

  onNumberFocus(): void {
    this.isNumberBeingEdited = true;
  }

  onNumberBlur(): void {
    this.isNumberBeingEdited = false;
    this.cdr.markForCheck();
  }

  get lpgaIdFormatted(): string {
    if (!this.tournament.importSourceId && this.tournament.importSourceId !== 0) return '';
    return this.tournament.importSourceId.toString();
  }

  set lpgaIdFormatted(value: string) {
    // Remove any non-digit characters
    const cleaned = value.replace(/\D/g, '');

    if (cleaned === '') {
      // Empty string defaults to undefined/null
      this.tournament.importSourceId = undefined;
      this.markDirty();
    } else {
      const numericValue = parseInt(cleaned, 10);
      if (!isNaN(numericValue) && numericValue <= 2147483647) {
        this.tournament.importSourceId = numericValue;
        this.markDirty();
      }
      // If value exceeds max, don't update (silently ignore)
    }
  }

  onLpgaIdFocus(): void {
    this.isLpgaIdBeingEdited = true;
  }

  onLpgaIdBlur(): void {
    this.isLpgaIdBeingEdited = false;
    this.cdr.markForCheck();
  }

  private updateSetting(key: string, value: string): void {
    if (!this.tournament.settings) {
      this.tournament.settings = {};
    }
    this.tournament.settings[key] = value;
  }

  private generateAvailableYears(): string[] {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];

    // Generate from 5 years prior to current year
    for (let i = 4; i >= 0; i--) {
      years.push((currentYear - i).toString());
    }

    // sort years in DESCENDING order
    years.sort((a, b) => parseInt(b) - parseInt(a));

    return years;
  }

  private generateTimeOffsetOptions(): string[] {
    const timeOffsets: string[] = [];

    // Generate from -12:00 to +12:00 with 30-minute increments
    // Start at -12:00 (which is -12 * 2 = -24 half-hours)
    // End at +12:00 (which is 12 * 2 = 24 half-hours)
    for (let halfHours = -24; halfHours <= 24; halfHours++) {
      const hours = Math.floor(Math.abs(halfHours) / 2);
      const minutes = (Math.abs(halfHours) % 2) * 30;
      const sign = halfHours >= 0 ? '+' : '-';

      const hoursStr = hours.toString().padStart(2, '0');
      const minutesStr = minutes.toString().padStart(2, '0');

      timeOffsets.push(`${sign}${hoursStr}:${minutesStr}`);
    }

    return timeOffsets;
  }
  statuses = [
    { value: '', display: '-- No Status --', description: 'Clears the tournament status' },
    { value: 'NotStarted', display: 'Not Started', description: 'Tournament not yet begun' },
    { value: 'InProgress', display: 'In Progress', description: 'Currently active' },
    { value: 'Delayed', display: 'Delayed', description: 'Temporarily postponed' },
    { value: 'Suspended', display: 'Suspended', description: 'Play suspended' },
    { value: 'Official', display: 'Official', description: 'Completed and official' }
  ];

  // Tournament notification properties
  private tournamentEventsSubscription?: Subscription;
  private routeSubscription?: Subscription;

  @ViewChild(ConfirmationDialogComponent) confirmationDialog!: ConfirmationDialogComponent;

  constructor(
    private route: ActivatedRoute,
    private tournamentService: TournamentService,
    private importService: ImportService,
    private toastrService: ToastrService,
    private refService: RefService,
    private tourService: TourService,
    private loggingService: LoggingService,
    private alertService: AlertService,
    private scoringRealtimeService: ScoringRealtimeService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    // Always fetch fresh tournament data when component initializes
    // This ensures we have up-to-date data even when navigating between child routes
    this.routeSubscription = this.route.params.pipe(
      switchMap(params => {
        const tournamentIdFromParams = params['tournamentId'];
        return this.tournamentService.getById(tournamentIdFromParams);
      })
    ).subscribe({
      next: (tournament) => {
        this.loggingService.debug('TournamentDetailComponent', `Tournament loaded: ${tournament?.name}, ID: ${tournament?.tournamentId}`);
        this.loggingService.debug('TournamentDetailComponent', `Tournament has ${tournament?.classifications?.length || 0} classifications`);

        this.tournament = tournament;

        // DO NOT update selectedTournament here - only update on save
        // This prevents the navigation dropdown from changing until changes are saved

        // Store initial status for change detection
        this.previousStatus = this.status;

        // Initialize external IDs view models
        this.initializeExternalIds();

        // Trigger change detection after setting tournament data
        this.cdr.markForCheck();

        // Load reference data first, then mark selected classifications
        forkJoin([
          this.getCountries(),
          this.getStates(),
          this.getTours(),
          this.getTournamentScoringTypes(),
          this.getAllClassifications(),
        ]).subscribe({
          next: () => {
            this.loggingService.debug('TournamentDetailComponent', 'All reference data loaded. About to mark selected classifications.');
            // After all reference data is loaded, mark selected classifications
            this.markSelectedClassifications();
            // Trigger change detection after reference data is loaded
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.loggingService.error('TournamentDetailComponent', 'Error loading reference data:', error);
          }
        });
      },
      error: (error) => {
        this.loggingService.error('TournamentDetailComponent', 'Error loading tournament data:', error);
        this.toastrService.error('Failed to load tournament data', 'Error');
      }
    });

    // Start listening to tournament events
    this.startListeningToTournamentEvents();
  }

  ngAfterViewInit(): void {
    // Set focus to LPGA ID field after view is initialized
    setTimeout(() => {
      this.lpgaIdInput?.nativeElement?.focus();
    }, 0);
  }

  ngOnDestroy() {
    this.stopListeningToTournamentEvents();

    // Clean up route subscription
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
      this.routeSubscription = undefined;
      this.loggingService.debug('TournamentDetailComponent', 'Unsubscribed from route subscription');
    }
  }

  getCountries(): Observable<Country[]> {
    return this.refService.getCountries().pipe(
      tap((countries) => {
        this.countries = countries;
        this.cdr.markForCheck();
      })
    );
  }

  getStates(): Observable<State[]> {
    return this.refService.getStates().pipe(
      tap((states) => {
        this.states = states;
        this.cdr.markForCheck();
      })
    );
  }

  getTours(): Observable<Tour[]> {
    return this.tourService.getAll().pipe(
      tap((tours) => {
        this.tours = tours;
        this.cdr.markForCheck();
      })
    );
  }

  getTournamentScoringTypes(): Observable<TournamentScoringType[]> {
    return this.tournamentService.getTournamentScoringTypes().pipe(
      tap((types) => {
        this.tournamentScoringTypes = types;
        this.cdr.markForCheck();
      })
    );
  }

  getAllClassifications(): Observable<TournamentClassification[]> {
    return this.tournamentService.getTournamentClassifications().pipe(
      tap((classifications) => {
        this.loggingService.debug('TournamentDetailComponent', `getAllClassifications received ${classifications?.length || 0} classifications from service`);
        this.tournamentClassifications = classifications;
        this.loggingService.debug('TournamentDetailComponent', 'tournamentClassifications array updated. Length:', this.tournamentClassifications.length);
        this.cdr.markForCheck();
      })
    );
  }

  private markSelectedClassifications(): void {
    this.loggingService.debug('TournamentDetailComponent', 'markSelectedClassifications called. Tournament classifications:', this.tournament.classifications);
    this.loggingService.debug('TournamentDetailComponent', 'Available tournamentClassifications:', this.tournamentClassifications.length);

    if (!this.tournament.classifications || !this.tournamentClassifications.length) {
      this.loggingService.debug('TournamentDetailComponent', 'Skipping markSelectedClassifications - missing data');
      return;
    }

    const selectedIds = new Set(
      this.tournament.classifications.map(c => c.tournamentClassificationID)
    );

    this.loggingService.debug('TournamentDetailComponent', 'Selected classification IDs:', Array.from(selectedIds));

    this.tournamentClassifications.forEach(classification => {
      classification.selected = selectedIds.has(classification.tournamentClassificationID);
    });

    this.loggingService.debug('TournamentDetailComponent', 'Updated classification selections:',
      this.tournamentClassifications.filter(c => c.selected).map(c => ({ id: c.tournamentClassificationID, desc: c.description })));
  }

  /**
   * Mark the form as dirty (has unsaved changes)
   */
  markDirty(): void {
    this.isDirty.set(true);
  }

  getSelectedStatusDisplay(): string {
    const selectedStatusObj = this.statuses.find(status => status.value === this.status);
    return selectedStatusObj ? selectedStatusObj.display : '';
  }

  onStatusChange(newStatus: string): void {
    // If status is changing and there's a status reason, ask user if they want to clear it
    if (newStatus !== this.previousStatus && (this.statusReason || '').trim() !== '') {
      // Store the new status temporarily in dialog data
      this.confirmationDialog.open({
        title: 'Clear Status Reason?',
        message: `You are changing the status. Would you like to clear the current status reason?<br><br><strong>Current reason:</strong> "${this.statusReason}"`,
        confirmButtonText: 'Clear Reason',
        cancelButtonText: 'Keep Reason',
        confirmButtonClass: 'btn-warning',
        data: newStatus
      });
    } else {
      // No status reason to worry about, just update
      this.previousStatus = newStatus;
      this.status = newStatus;
    }
  }

  onStatusReasonConfirmed(newStatus: string): void {
    this.statusReason = '';
    this.status = newStatus;
    this.previousStatus = newStatus;
  }

  onStatusReasonCancelled(): void {
    // User cancelled - keep the existing reason and update to the new status
    const newStatus = this.confirmationDialog.data;
    this.status = newStatus;
    this.previousStatus = newStatus;
  }

  formatDateForInput(date: Date | null): string {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  }

  parseInputDate(dateString: string): Date {
    return new Date(dateString);
  }

  save(): void {
    // Validate external IDs before saving
    const externalIdError = this.validateExternalIds();
    if (externalIdError) {
      this.toastrService.error(externalIdError);
      return;
    }

    // Collect selected classification IDs
    // this.loggingService.debug('TournamentDetailComponent', 'All tournamentClassifications before filtering:',
    //   this.tournamentClassifications.map(c => ({
    //     id: c.tournamentClassificationID,
    //     desc: c.description,
    //     selected: c.selected
    //   })));

    const selectedClassificationIds = this.tournamentClassifications
      .filter(c => c.selected)
      .map(c => c.tournamentClassificationID!);

    // Map external IDs to request format
    const externalIdsRequest: ModulesTournamentsApplicationTournamentExternalIdsTournamentExternalIdRequest[] =
      this.externalIds.map(extId => ({
        organization: extId.organization,
        externalId: extId.externalId
      }));

    // Add classifications and external IDs to tournament object
    const tournamentToSave = {
      ...this.tournament,
      classificationIds: selectedClassificationIds,
      externalIds: externalIdsRequest
    };

    this.isSaving.set(true);

    // this.loggingService.debug('TournamentDetailComponent', 'Saving tournament with classificationIds:', selectedClassificationIds);

    this.tournamentService
      .updateTournament(this.tournament.tournamentId!, tournamentToSave)
      .subscribe({
        next: (tournament) => {
          // this.loggingService.debug('TournamentDetailComponent', 'Tournament saved. Returned classifications:', tournament.classifications);

          if (tournament.modifiedOnUtc !== this.tournament.modifiedOnUtc) {
            this.toastrService.success('Tournament saved', 'Success');
          }
          this.tournament = { ...tournament } as Tournament;

          // Update the selected tournament in the service ONLY after successful save
          // This ensures the navigation dropdown updates with the saved tournament data
          this.tournamentService.selectedTournament = this.tournament;

          this.markSelectedClassifications();
          this.initializeExternalIds();
          // Reset dirty state after successful save
          this.isDirty.set(false);
          this.isSaving.set(false);
          // Trigger change detection after save
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.toastrService.error('Failed to save tournament', 'Error');
          this.isSaving.set(false);
        }
      });
  }

  /**
   * Validate external IDs and return error message if any validation fails.
   */
  private validateExternalIds(): string | null {
    for (let i = 0; i < this.externalIds.length; i++) {
      const extId = this.externalIds[i];
      if (extId.isEditing) {
        return `Please save or remove the external ID at row ${i + 1} before saving the tournament.`;
      }
      if (!extId.organization || !extId.organization.trim()) {
        return `External ID at row ${i + 1} has an empty Organization.`;
      }
    }
    return null;
  }

  setActive(): void {
    this.tournamentService
      .markTournamentActive(this.tournament.tournamentId!)
      .subscribe({
        next: () => {
          this.tournament.active = true;
          this.tournamentService.selectedTournament = this.tournament;
          this.cdr.markForCheck();
          this.toastrService.success('Tournament status updated', 'Success');
        },
        error: (error) => {
          this.loggingService.error('TournamentDetailComponent', 'Failed to mark tournament active', error);
          this.toastrService.error('Failed to update tournament status', 'Error');
        }
      });
  }

  importPlayers(): void {
    this.importService
      .importPlayers(this.tournament.importSourceId!)
      .subscribe({
        next: (response) => {
          this.toastrService.success('Players imported successfully!', 'Success');
        },
        error: (error) => {
          this.toastrService.error('Failed to import players!', 'Error');
        },
      });
  }

  importCourses(): void {
    this.importService
      .importCourses(this.tournament.importSourceId!)
      .subscribe({
        next: (response) => {
          this.toastrService.success('Courses imported successfully!', 'Success');
        },
        error: (error) => {
          this.toastrService.error('Failed to import courses!', 'Error');
        },
      });
  }

  /**
   * Start listening to real-time tournament events
   */
  private startListeningToTournamentEvents(): void {
    this.tournamentEventsSubscription = this.scoringRealtimeService.tournamentActivity.subscribe({
      next: (event: TournamentActivityData) => {
        this.handleTournamentEvent(event);
      },
      error: (error) => {
        this.loggingService.error('TournamentDetailComponent', 'Error in tournament events subscription:', error);
      }
    });
    this.loggingService.debug('TournamentDetailComponent', 'Started listening to real-time tournament events');
  }

  /**
   * Stop listening to real-time tournament events
   */
  private stopListeningToTournamentEvents(): void {
    if (this.tournamentEventsSubscription) {
      this.tournamentEventsSubscription.unsubscribe();
      this.tournamentEventsSubscription = undefined;
      this.loggingService.debug('TournamentDetailComponent', 'Unsubscribed from real-time tournament events');
    }
  }

  /**
   * Handle incoming tournament events
   */
  private handleTournamentEvent(event: TournamentActivityData): void {
    this.loggingService.debug('TournamentDetailComponent', 'Processing tournament event:', event);

    // Only show notifications for the current tournament
    if (this.tournament?.tournamentId !== event.tournamentId) {
      this.loggingService.debug('TournamentDetailComponent', 'Ignoring event for different tournament');
      return;
    }

    const title = event.type === 'TournamentMarkedActive' ? 'Tournament Activated' : 'Tournament Deactivated';
    const message = event.type === 'TournamentMarkedActive'
      ? `Tournament "${this.tournament.name}" has been marked active.`
      : `Tournament "${this.tournament.name}" has been marked not active.`;

    try {
      const alertId = event.type === 'TournamentMarkedActive'
        ? this.alertService.success(title, message, {
          dismissible: true,
          autoClose: true,
          autoCloseDelay: 30000, // Auto-close after 30 seconds
          category: 'tournament'
        })
        : this.alertService.warning(title, message, {
          dismissible: true,
          autoClose: true,
          autoCloseDelay: 30000, // Auto-close after 30 seconds
          category: 'tournament'
        });

      this.loggingService.debug('TournamentDetailComponent', `Tournament alert notification shown successfully with ID: ${alertId}`);
    } catch (error) {
      this.loggingService.error('TournamentDetailComponent', 'Error showing tournament alert notification', error);
    }
  }

  /**
   * Initialize external IDs from tournament data
   */
  private initializeExternalIds(): void {
    this.externalIds = (this.tournament.externalIds || []).map(extId => ({
      organization: extId.organization || '',
      externalId: extId.externalId?.toString() ?? '',
      isEditing: false
    }));
  }

  /**
   * Add a new external ID
   */
  addExternalId(): void {
    this.externalIds.push({
      organization: '',
      externalId: '',
      isEditing: true
    });
    this.markDirty();
    this.cdr.markForCheck();
  }

  /**
   * Edit an existing external ID
   */
  editExternalId(index: number): void {
    this.externalIds[index].isEditing = true;
    this.cdr.markForCheck();
  }

  /**
   * Save external ID changes
   */
  saveExternalId(index: number): void {
    const extId = this.externalIds[index];

    // Validate
    if (!extId.organization || !extId.organization.trim()) {
      this.toastrService.error('Organization is required');
      return;
    }

    if (!extId.externalId || extId.externalId.toString().trim().length === 0) {
      this.toastrService.error('External ID is required');
      return;
    }

    extId.isEditing = false;
    this.markDirty();
    this.cdr.markForCheck();
  }

  /**
   * Remove an external ID
   */
  removeExternalId(index: number): void {
    this.externalIds.splice(index, 1);
    this.markDirty();
    this.cdr.markForCheck();
  }

  /**
   * Handle changes to importSourceId and sync with LPGA external ID
   */
  onImportSourceIdChange(): void {
    this.markDirty();

    // Find LPGA external ID and update it
    const lpgaExternalId = this.externalIds.find(extId =>
      extId.organization?.toUpperCase() === 'LPGA'
    );

    if (lpgaExternalId && this.tournament.importSourceId) {
      lpgaExternalId.externalId = this.tournament.importSourceId.toString();
    } else if (!lpgaExternalId && this.tournament.importSourceId) {
      // If no LPGA external ID exists, create one
      this.externalIds.push({
        organization: 'LPGA',
        externalId: this.tournament.importSourceId.toString(),
        isEditing: false
      });
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle changes to external ID and sync with importSourceId if it's LPGA
   */
  onExternalIdChange(index: number): void {
    const extId = this.externalIds[index];

    // If this is the LPGA external ID, sync it with importSourceId
    if (extId.organization?.toUpperCase() === 'LPGA') {
      const numericValue = parseInt(extId.externalId, 10);
      if (!isNaN(numericValue)) {
        this.tournament.importSourceId = numericValue;
      }
    }
  }
}
